/**
 * Element ID Registry — Single Source of Truth for all interactive element IDs.
 *
 * Every button, link, input, and clickable element gets a unique, descriptive ID
 * registered here. This guarantees:
 *   - Compile-time uniqueness (duplicate keys = TypeScript error)
 *   - Stable Playwright selectors (#my-element-btn)
 *   - Accessibility linking (aria-describedby, aria-labelledby, htmlFor)
 *   - Unambiguous agent/developer communication
 *
 * Naming: <page/area>-<action/role>-<context>-<suffix>
 * Suffixes: -btn, -link, -input, -toggle, -tab, -dropdown, -error, -label, -region
 *
 * Usage:
 *   import { EID } from '@/lib/element-ids.registry';
 *   <Button id={EID['dashboard-create-workspace-btn']}>Create</Button>
 */

export const EID = {
  // ─── Landing: Nav ────────────────────────────────────────────────────
  'landing-nav-home-link': 'landing-nav-home-link',
  'landing-nav-tools-link': 'landing-nav-tools-link',
  'landing-nav-pricing-link': 'landing-nav-pricing-link',
  'landing-nav-faq-link': 'landing-nav-faq-link',
  'landing-nav-language-dropdown': 'landing-nav-language-dropdown',
  'landing-nav-login-link': 'landing-nav-login-link',
  'landing-nav-register-link': 'landing-nav-register-link',

  // ─── Landing: Hero ───────────────────────────────────────────────────
  'landing-hero-register-btn': 'landing-hero-register-btn',
  'landing-hero-explore-tools-link': 'landing-hero-explore-tools-link',

  // ─── Landing: Tool Categories ────────────────────────────────────────
  'landing-tools-studio-link': 'landing-tools-studio-link',
  'landing-tools-text-link': 'landing-tools-text-link',
  'landing-tools-amazon-link': 'landing-tools-amazon-link',
  'landing-tools-utilities-link': 'landing-tools-utilities-link',

  // ─── Landing: Variants ──────────────────────────────────────────────
  // (no interactive elements — bento grid is display-only)

  // ─── Landing: Free Tools ────────────────────────────────────────────
  'landing-freetools-converter-link': 'landing-freetools-converter-link',
  'landing-freetools-bytecounter-link': 'landing-freetools-bytecounter-link',
  'landing-freetools-more-link': 'landing-freetools-more-link',

  // ─── Landing: FAQ ───────────────────────────────────────────────────
  'landing-faq-toggle-1-btn': 'landing-faq-toggle-1-btn',
  'landing-faq-toggle-2-btn': 'landing-faq-toggle-2-btn',
  'landing-faq-toggle-3-btn': 'landing-faq-toggle-3-btn',
  'landing-faq-toggle-4-btn': 'landing-faq-toggle-4-btn',
  'landing-faq-toggle-5-btn': 'landing-faq-toggle-5-btn',
  'landing-faq-answer-1-region': 'landing-faq-answer-1-region',
  'landing-faq-answer-2-region': 'landing-faq-answer-2-region',
  'landing-faq-answer-3-region': 'landing-faq-answer-3-region',
  'landing-faq-answer-4-region': 'landing-faq-answer-4-region',
  'landing-faq-answer-5-region': 'landing-faq-answer-5-region',

  // ─── Landing: CTA ───────────────────────────────────────────────────
  'landing-cta-register-btn': 'landing-cta-register-btn',

  // ─── Landing: Footer ────────────────────────────────────────────────
  'landing-footer-home-link': 'landing-footer-home-link',
  'landing-footer-tools-link': 'landing-footer-tools-link',
  'landing-footer-pricing-link': 'landing-footer-pricing-link',
  'landing-footer-register-link': 'landing-footer-register-link',
  'landing-footer-faq-link': 'landing-footer-faq-link',
  'landing-footer-login-link': 'landing-footer-login-link',
  'landing-footer-imprint-link': 'landing-footer-imprint-link',
  'landing-footer-privacy-link': 'landing-footer-privacy-link',
  'landing-footer-terms-link': 'landing-footer-terms-link',
  // ─── Product Detail: Hero ───────────────────────────────────────────
  'product-hero-sync-btn': 'product-hero-sync-btn',
  'product-hero-more-actions-btn': 'product-hero-more-actions-btn',
  'product-hero-manage-images-btn': 'product-hero-manage-images-btn',
  'product-hero-copy-sku-btn': 'product-hero-copy-sku-btn',
  'product-hero-copy-ean-btn': 'product-hero-copy-ean-btn',
  'product-hero-copy-asin-btn': 'product-hero-copy-asin-btn',
  'product-hero-copy-external-id-btn': 'product-hero-copy-external-id-btn',

  // ─── Product Detail: Readiness Banner ─────────────────────────────
  'product-readiness-start-btn': 'product-readiness-start-btn',

  // ─── Product Detail: Tabs ─────────────────────────────────────────
  'product-tab-overview': 'product-tab-overview',
  'product-tab-masterdata': 'product-tab-masterdata',
  'product-tab-content': 'product-tab-content',
  'product-tab-assets': 'product-tab-assets',
  'product-tab-channels': 'product-tab-channels',
  'product-tab-quick-edit': 'product-tab-quick-edit',
  'product-tab-automation': 'product-tab-automation',
  'product-tab-history': 'product-tab-history',
  'product-tab-rawdata': 'product-tab-rawdata',
  'product-tab-performance': 'product-tab-performance',

  // ─── Global Nav ───────────────────────────────────────────────────
  'nav-theme-toggle': 'nav-theme-toggle',

  // ─── Product Detail: Media — Channel Filter (M1) ──────────────────
  'product-images-channel-filter': 'product-images-channel-filter',
  'product-images-filter-all-btn': 'product-images-filter-all-btn',
  'product-images-library-panel': 'product-images-library-panel',

  // ─── Product Detail: Content — Feldlisten-Editor (C1) ─────────────
  'content-channel-select': 'content-channel-select',
  'content-channel-language-select': 'content-channel-language-select',
  'content-field-versions-toggle': 'content-field-versions-toggle',
  'content-field-save-btn': 'content-field-save-btn',
  'content-field-discard-btn': 'content-field-discard-btn',
  'content-version-preview-btn': 'content-version-preview-btn',
  'content-version-restore-btn': 'content-version-restore-btn',
  'content-version-compare-btn': 'content-version-compare-btn',
  'content-connector-show-inherited-toggle': 'content-connector-show-inherited-toggle',
  'content-connector-sync-btn': 'content-connector-sync-btn',
  'content-connector-menu-btn': 'content-connector-menu-btn',
  'content-sidebyside-toggle': 'content-sidebyside-toggle',
  'content-activity-filter-all-btn': 'content-activity-filter-all-btn',
  'content-activity-filter-connector-btn': 'content-activity-filter-connector-btn',
  'content-activity-show-btn': 'content-activity-show-btn',
  'content-activity-restore-btn': 'content-activity-restore-btn',
  'content-activity-history-btn': 'content-activity-history-btn',

  // ─── Product Detail: Stammdaten (Master Data) Tab ─────────────────
  'product-masterdata-tab': 'product-masterdata-tab',
  'product-masterdata-empty': 'product-masterdata-empty',
  'masterdata-card-categories': 'masterdata-card-categories',
  'masterdata-card-identifiers': 'masterdata-card-identifiers',
  'masterdata-card-prices': 'masterdata-card-prices',
  'masterdata-card-dimensions': 'masterdata-card-dimensions',
  'masterdata-card-stock': 'masterdata-card-stock',
  'masterdata-card-channels': 'masterdata-card-channels',
  'masterdata-card-status': 'masterdata-card-status',

  // ─── Product Detail: Overview Tab ─────────────────────────────────
  'product-overview-edit-content-link': 'product-overview-edit-content-link',
  'product-overview-view-channels-link': 'product-overview-view-channels-link',
  'product-overview-add-channel-btn': 'product-overview-add-channel-btn',

  // ─── Dashboard ─────────────────────────────────────────────────────
  'dashboard-heading': 'dashboard-heading',
  'dashboard-header-gallery-link': 'dashboard-header-gallery-link',
  'dashboard-header-generate-btn': 'dashboard-header-generate-btn',
  'dashboard-email-warning-region': 'dashboard-email-warning-region',
  'dashboard-stats-credits-region': 'dashboard-stats-credits-region',
  'dashboard-stats-images-region': 'dashboard-stats-images-region',
  'dashboard-stats-products-region': 'dashboard-stats-products-region',

  // ─── Dashboard: Favorite Tools ─────────────────────────────────────
  'dashboard-favorites-section': 'dashboard-favorites-section',
  'dashboard-favorites-all-tools-link': 'dashboard-favorites-all-tools-link',
  'dashboard-favorites-list': 'dashboard-favorites-list',

  // ─── Dashboard: Recent Images ──────────────────────────────────────
  'dashboard-images-section': 'dashboard-images-section',
  'dashboard-images-gallery-link': 'dashboard-images-gallery-link',
  'dashboard-images-create-btn': 'dashboard-images-create-btn',
  'dashboard-images-batch-badge': 'dashboard-images-batch-badge',

  // ─── Dashboard: Quick Actions ──────────────────────────────────────
  'dashboard-quick-generate-link': 'dashboard-quick-generate-link',
  'dashboard-quick-credits-link': 'dashboard-quick-credits-link',
  'dashboard-quick-settings-link': 'dashboard-quick-settings-link',

  // ─── Dashboard: Transactions ───────────────────────────────────────
  'dashboard-transactions-section': 'dashboard-transactions-section',
  'dashboard-transactions-all-link': 'dashboard-transactions-all-link',

  // ─── Dashboard: Plan Card ──────────────────────────────────────────
  'dashboard-plan-section': 'dashboard-plan-section',
  'dashboard-plan-upgrade-link': 'dashboard-plan-upgrade-link',
  'dashboard-plan-topup-btn': 'dashboard-plan-topup-btn',

  // ─── Amazon Audit: Potential Check ─────────────────────────────────
  'audit-potential-check-region': 'audit-potential-check-region',
  'audit-potential-remake-btn': 'audit-potential-remake-btn',
  'audit-potential-view-full-btn': 'audit-potential-view-full-btn',

  // ─── Text Tools: Brand (Product Brands ai_description) ─────────────
  'text-tool-brand-dropdown': 'text-tool-brand-dropdown',
  'text-vorlage-brand-dropdown': 'text-vorlage-brand-dropdown',

  // ─── Shared: Vision Image Picker ───────────────────────────────────
  'vision-image-file-input': 'vision-image-file-input',
  'vision-image-clear-btn': 'vision-image-clear-btn',

  // ─── Text Tools: Multi-Variant + Refine ────────────────────────────
  'text-tool-variants-dropdown': 'text-tool-variants-dropdown',
  'text-tool-variant-switcher-region': 'text-tool-variant-switcher-region',
  'text-tool-refine-shorter-btn': 'text-tool-refine-shorter-btn',
  'text-tool-refine-longer-btn': 'text-tool-refine-longer-btn',
  'text-tool-refine-casual-btn': 'text-tool-refine-casual-btn',
  'text-tool-refine-formal-btn': 'text-tool-refine-formal-btn',
  'text-tool-refine-with_hashtags-btn': 'text-tool-refine-with_hashtags-btn',
  'text-tool-refine-new_hook-btn': 'text-tool-refine-new_hook-btn',

  // ─── Profile: MFA Enrollment ───────────────────────────────────────
  'mfa-qr': 'mfa-qr',
  'mfa-secret': 'mfa-secret',
  'mfa-copy-secret': 'mfa-copy-secret',
  'mfa-enroll-code': 'mfa-enroll-code',
  'mfa-enroll-confirm': 'mfa-enroll-confirm',
  'mfa-download-codes': 'mfa-download-codes',
  'mfa-recovery-codes': 'mfa-recovery-codes',
  'mfa-factor-list': 'mfa-factor-list',
  'mfa-add-factor': 'mfa-add-factor',

  // ─── Auth: MFA Challenge (Login 2nd factor) ────────────────────────
  'mfa-challenge-code': 'mfa-challenge-code',
  'mfa-challenge-verify': 'mfa-challenge-verify',
  'mfa-use-recovery': 'mfa-use-recovery',

  // ─── Admin: MFA Enforcement Banner ─────────────────────────────────
  'mfa-enforcement-banner': 'mfa-enforcement-banner',
  'mfa-banner-setup': 'mfa-banner-setup',

  // ─── Templates: Page (A — Refined) ─────────────────────────────────
  'templates-marketplace-link': 'templates-marketplace-link',
  'templates-new-template-btn': 'templates-new-template-btn',
  'templates-creator-pill-link': 'templates-creator-pill-link',
  'templates-info-strip-toggle': 'templates-info-strip-toggle',
  'templates-tab-templates-tab': 'templates-tab-templates-tab',
  'templates-tab-collections-tab': 'templates-tab-collections-tab',
  'templates-search-input': 'templates-search-input',
  'templates-type-all-btn': 'templates-type-all-btn',
  'templates-type-image-btn': 'templates-type-image-btn',
  'templates-type-text-btn': 'templates-type-text-btn',
  'templates-status-dropdown': 'templates-status-dropdown',
  'templates-view-list-btn': 'templates-view-list-btn',
  'templates-view-grid-btn': 'templates-view-grid-btn',
  'templates-sort-dropdown': 'templates-sort-dropdown',
  'templates-new-card-btn': 'templates-new-card-btn',

  // ─── Product Detail: History Tab ──────────────────────────────────
  'product-history-filter-all-btn': 'product-history-filter-all-btn',
  'product-history-filter-content-btn': 'product-history-filter-content-btn',
  'product-history-filter-assets-btn': 'product-history-filter-assets-btn',
  'product-history-channel-select': 'product-history-channel-select',
  'product-history-entry': 'product-history-entry',

  // ─── Product Detail: Performance Tab ──────────────────────────────
  'performance-kpi-revenue': 'performance-kpi-revenue',
  'performance-kpi-quantity': 'performance-kpi-quantity',
  'performance-metric-toggle': 'performance-metric-toggle',
  'performance-granularity-toggle': 'performance-granularity-toggle',
  'performance-range-select': 'performance-range-select',
  'performance-compare-select': 'performance-compare-select',
  'performance-channel-select': 'performance-channel-select',
  'performance-event-list': 'performance-event-list',
  'performance-event-item': 'performance-event-item',

  // Listing Builder (Stufe 5)
  'listing-builder-market-select': 'listing-builder-market-select',
  'listing-builder-market-tld-select': 'listing-builder-market-tld-select',
  'listing-builder-start-remake-btn': 'listing-builder-start-remake-btn',
  // Listing Builder — Source-Selection Dialog (Stufe 5b)
  'listing-builder-source-dialog': 'listing-builder-source-dialog',
  'listing-builder-source-tab-asin': 'listing-builder-source-tab-asin',
  'listing-builder-source-tab-channel': 'listing-builder-source-tab-channel',
  'listing-builder-source-tab-manual': 'listing-builder-source-tab-manual',
  'listing-builder-asin-input': 'listing-builder-asin-input',
  'listing-builder-asin-submit-btn': 'listing-builder-asin-submit-btn',
  'listing-builder-manual-title-input': 'listing-builder-manual-title-input',
  'listing-builder-manual-bullets-input': 'listing-builder-manual-bullets-input',
  'listing-builder-manual-description-input': 'listing-builder-manual-description-input',
  'listing-builder-manual-brand-input': 'listing-builder-manual-brand-input',
  'listing-builder-manual-submit-btn': 'listing-builder-manual-submit-btn',
  'listing-builder-channel-submit-btn': 'listing-builder-channel-submit-btn',
  // Listing Builder — Remake Studio (Stufe 5b-4)
  'listing-builder-remake-version-select': 'listing-builder-remake-version-select',
  'listing-builder-remake-slot-card': 'listing-builder-remake-slot-card',
  'listing-builder-remake-field-title': 'listing-builder-remake-field-title',
  'listing-builder-remake-field-bullets': 'listing-builder-remake-field-bullets',
  'listing-builder-remake-field-description': 'listing-builder-remake-field-description',
  'listing-builder-remake-cancel-btn': 'listing-builder-remake-cancel-btn',
  'listing-builder-remake-adopt-btn': 'listing-builder-remake-adopt-btn',
  // Listing Builder — Overview + Detail (Stufe 5b-2)
  'listing-builder-overview': 'listing-builder-overview',
  'listing-builder-new-audit-btn': 'listing-builder-new-audit-btn',
  'listing-builder-search-input': 'listing-builder-search-input',
  'listing-builder-audit-card': 'listing-builder-audit-card',
  'listing-builder-card-delete-btn': 'listing-builder-card-delete-btn',
  'listing-builder-detail': 'listing-builder-detail',
  'listing-builder-detail-back-link': 'listing-builder-detail-back-link',
  'listing-builder-detail-tab-report': 'listing-builder-detail-tab-report',
  'listing-builder-detail-tab-remake': 'listing-builder-detail-tab-remake',
  // Listing Builder — Feature-Parity
  'listing-builder-credit-hint': 'listing-builder-credit-hint',
  'listing-builder-report-view-toggle': 'listing-builder-report-view-toggle',
  'listing-builder-report-view-compact': 'listing-builder-report-view-compact',
  'listing-builder-report-view-full': 'listing-builder-report-view-full',
  'listing-builder-audit-version-select': 'listing-builder-audit-version-select',
  'listing-builder-asin-batch-toggle': 'listing-builder-asin-batch-toggle',
  'listing-builder-asin-batch-input': 'listing-builder-asin-batch-input',
  'listing-builder-asin-batch-submit-btn': 'listing-builder-asin-batch-submit-btn',
  'listing-builder-share-toggle-btn': 'listing-builder-share-toggle-btn',
  'listing-builder-share-copy-btn': 'listing-builder-share-copy-btn',
  'listing-builder-report-amazon-link': 'listing-builder-report-amazon-link',
  'listing-builder-reaudit-btn': 'listing-builder-reaudit-btn',

  // ─── Bulk-Text-Wizard (text-bulk/wizard) ───────────────────────────
  'text-bulk-wizard-stepper-region': 'text-bulk-wizard-stepper-region',
  'text-bulk-wizard-channel-list-region': 'text-bulk-wizard-channel-list-region',
  'text-bulk-wizard-research-toggle': 'text-bulk-wizard-research-toggle',
  'text-bulk-wizard-back-btn': 'text-bulk-wizard-back-btn',
  'text-bulk-wizard-next-btn': 'text-bulk-wizard-next-btn',
  'text-bulk-wizard-start-btn': 'text-bulk-wizard-start-btn',

  // ─── Bulk-Text Readiness-Einstiegsfilter (text-bulk, T12) ──────────
  'text-bulk-issues-entry-channel-dropdown': 'text-bulk-issues-entry-channel-dropdown',
  'text-bulk-issues-entry-load-btn': 'text-bulk-issues-entry-load-btn',

  // ─── Optimierungen-Karte (A1, wiederverwendet in Drawer + Bulk-Text-Review T7) ───
  'optimierung-card-apply-btn': 'optimierung-card-apply-btn',
  'optimierung-card-regenerate-btn': 'optimierung-card-regenerate-btn',
  'optimierung-card-discard-btn': 'optimierung-card-discard-btn',
  'optimierung-card-edit-btn': 'optimierung-card-edit-btn',

  // ─── Bulk-Text-Review (text-bulk/review, T7) ───────────────────────
  'text-bulk-review-tab-needs-review': 'text-bulk-review-tab-needs-review',
  'text-bulk-review-tab-ready': 'text-bulk-review-tab-ready',
  'text-bulk-review-retry-btn': 'text-bulk-review-retry-btn',
  'text-bulk-review-pagination-region': 'text-bulk-review-pagination-region',
  'text-bulk-review-inline-edit-textarea': 'text-bulk-review-inline-edit-textarea',
  'text-bulk-review-inline-edit-save-btn': 'text-bulk-review-inline-edit-save-btn',
  'text-bulk-review-inline-edit-cancel-btn': 'text-bulk-review-inline-edit-cancel-btn',
  'text-bulk-review-discard-dialog-reason-input': 'text-bulk-review-discard-dialog-reason-input',
  'text-bulk-review-discard-dialog-confirm-btn': 'text-bulk-review-discard-dialog-confirm-btn',
  'text-bulk-review-discard-dialog-cancel-btn': 'text-bulk-review-discard-dialog-cancel-btn',

  // ─── Bulk-Text „Bereit"-Tab (text-bulk/review, T8) ─────────────────
  'text-bulk-ready-gate-region': 'text-bulk-ready-gate-region',
  'text-bulk-ready-row-toggle-btn': 'text-bulk-ready-row-toggle-btn',
  'text-bulk-ready-row-apply-btn': 'text-bulk-ready-row-apply-btn',
  'text-bulk-ready-row-edit-btn': 'text-bulk-ready-row-edit-btn',
  'text-bulk-ready-row-discard-btn': 'text-bulk-ready-row-discard-btn',
  'text-bulk-ready-approve-all-btn': 'text-bulk-ready-approve-all-btn',
  'text-bulk-ready-result-region': 'text-bulk-ready-result-region',
  'text-bulk-ready-pagination-region': 'text-bulk-ready-pagination-region',

  // ─── Bulk-Text Undo/Push-Retry (text-bulk/review, T13/T14, P2) ─────
  'text-bulk-ready-undo-all-btn': 'text-bulk-ready-undo-all-btn',
  'text-bulk-ready-retry-push-btn': 'text-bulk-ready-retry-push-btn',

  // ─── Bulk-Text Ergebnis-Dialog (text-bulk/review, T13, P4/B6a) ─────
  'text-bulk-apply-result-dialog': 'text-bulk-apply-result-dialog',
  'text-bulk-apply-result-back': 'text-bulk-apply-result-back',
  'text-bulk-apply-result-view-details-btn': 'text-bulk-apply-result-view-details-btn',
  'text-bulk-apply-result-next-cta-btn': 'text-bulk-apply-result-next-cta-btn',

  // ─── Optimierungen: NBA-Karten (P4/B7, Task 10) ────────────────────
  'issues-nba-start-run': 'issues-nba-start-run',
  'issues-nba-open-rules': 'issues-nba-open-rules',
  'issues-nba-open-reviews': 'issues-nba-open-reviews',

  // ─── Admin: Model Approval Wizard ───────────────────────────────────
  'model-approval-open-wizard-btn': 'model-approval-open-wizard-btn',
  'model-approval-deactivate-btn': 'model-approval-deactivate-btn',
  'model-approval-discard-btn': 'model-approval-discard-btn',
  'model-approval-restore-btn': 'model-approval-restore-btn',
  'model-approval-wizard-dialog': 'model-approval-wizard-dialog',
  'model-approval-stepper': 'model-approval-stepper',
  'model-approval-probe-reachability-check-btn': 'model-approval-probe-reachability-check-btn',
  'model-approval-probe-input_text-check-btn': 'model-approval-probe-input_text-check-btn',
  'model-approval-probe-input_image-check-btn': 'model-approval-probe-input_image-check-btn',
  'model-approval-probe-output_text-check-btn': 'model-approval-probe-output_text-check-btn',
  'model-approval-probe-output_image-check-btn': 'model-approval-probe-output_image-check-btn',
  'model-approval-pricing-tier-select': 'model-approval-pricing-tier-select',
  'model-approval-pricing-input-price-input': 'model-approval-pricing-input-price-input',
  'model-approval-pricing-output-price-input': 'model-approval-pricing-output-price-input',
  'model-approval-pricing-min-bound-input': 'model-approval-pricing-min-bound-input',
  'model-approval-pricing-max-bound-input': 'model-approval-pricing-max-bound-input',
  'model-approval-previous-step-btn': 'model-approval-previous-step-btn',
  'model-approval-next-step-btn': 'model-approval-next-step-btn',
  'model-approval-activate-btn': 'model-approval-activate-btn',
  'model-approval-deactivate-confirm-dialog': 'model-approval-deactivate-confirm-dialog',
  'model-approval-deactivate-confirm-btn': 'model-approval-deactivate-confirm-btn',
  'model-approval-readiness-check-btn': 'model-approval-readiness-check-btn',
  'model-approval-review-active-btn': 'model-approval-review-active-btn',

  // ─── Admin: Model Registry Filter ───────────────────────────────────
  'model-management-provider-filter-select': 'model-management-provider-filter-select',
  'model-management-status-active-btn': 'model-management-status-active-btn',
  'model-management-status-available-btn': 'model-management-status-available-btn',
  'model-management-status-discarded-btn': 'model-management-status-discarded-btn',
} as const satisfies Record<string, string>;

export type ElementId = keyof typeof EID;
